import { computed, onBeforeUnmount, onMounted, ref } from "vue";

/**
 * Kalendarz (@vuepic/vue-datepicker) na mniejszych ekranach.
 *
 * Popup z `time-picker-inline` ma ok. 470 px wysokości, a pola „Od/Do" siedzą
 * w środku strony. Na laptopowych oknach (niski viewport) nie było go gdzie
 * rozwinąć: biblioteka mierzy menu „na próbę" wewnątrz wąskiego `.ds-panel`,
 * przez co myli się w pomiarze i jej auto-pozycjonowanie wypuszczało kalendarz
 * poza prawą i dolną krawędź ekranu — dolny pasek z „Anuluj/Wybierz" znikał
 * pod krawędzią okna.
 *
 * Zamiast walczyć z pomiarem biblioteki, na takich ekranach przechodzimy na
 * tryb wyśrodkowany (`teleport-center`): kalendarz jest przyklejony do środka
 * viewportu, więc zawsze widoczny w całości, niezależnie od tego, gdzie na
 * stronie stoi pole. Na dużych monitorach zostaje dotychczasowy popup „przy
 * polu", bo tam się swobodnie mieści.
 */
const COMPACT_QUERY = "(max-height: 1000px), (max-width: 1200px)";

export function useCompactDatePicker() {
  // true → kalendarz wyśrodkowany na ekranie, false → popup przy polu
  const compactDatePicker = ref(false);
  let mediaQuery = null;

  const sync = () => {
    compactDatePicker.value = Boolean(mediaQuery && mediaQuery.matches);
  };

  onMounted(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    mediaQuery = window.matchMedia(COMPACT_QUERY);
    sync();
    // Safari < 14 nie zna `addEventListener` na MediaQueryList — stąd fallback.
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", sync);
    } else if (typeof mediaQuery.addListener === "function") {
      mediaQuery.addListener(sync);
    }
  });

  onBeforeUnmount(() => {
    if (!mediaQuery) return;
    if (typeof mediaQuery.removeEventListener === "function") {
      mediaQuery.removeEventListener("change", sync);
    } else if (typeof mediaQuery.removeListener === "function") {
      mediaQuery.removeListener(sync);
    }
    mediaQuery = null;
  });

  // Klasa menu — w trybie wyśrodkowanym dokładamy modyfikator, po którym
  // `design-system.css` rysuje przygaszone tło pod kalendarzem.
  const datePickerMenuClass = computed(() =>
    compactDatePicker.value
      ? "ds-datepicker-menu ds-datepicker-menu--centered"
      : "ds-datepicker-menu"
  );

  return { compactDatePicker, datePickerMenuClass };
}
